import {
  Actions,
  ResultStatus,
  Role,
  Subjects,
  User,
} from '@dispatch/shared/types';
import { PaginatedQueryResult, useTableState } from '@dispatch/shared/hooks';
import {
  activeColumn,
  boldLinkColumn,
  CreateNewEntityButton,
  dateColumn,
  PageComponent,
  TableComponent,
  TableProps,
  textColumn,
} from '@dispatch/web/ui';
import { Box } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Ability } from '@casl/ability';
import { v4 as uuidv4 } from 'uuid';

export interface UsersProps {
  rows?: User[];
  status: ResultStatus;
  onSearch: (value: string) => void;
  permissions: Ability<[Actions, Subjects]> | undefined;
  usersData: PaginatedQueryResult<User[], unknown>;
  onPageSizeChange: (pageSize: number) => void;
  roles?: Role[];
}

export function Users({
  rows = [],
  status,
  onSearch,
  permissions,
  usersData,
  onPageSizeChange,
  roles,
}: UsersProps) {
  const { tableState, setTableState } = useTableState();
  const [data, setData] = useState<User[]>([]);
  const navigate = useNavigate();
  const mapRolesToRow = React.useMemo(() => {
    return rows.map((row) => {
      const role = roles?.find((r) => row.roleId === r.id);
      return {
        ...row,
        roleName: role?.name,
      };
    });
  }, [roles, rows]);

  useEffect(() => {
    setData([...mapRolesToRow]);
  }, [mapRolesToRow]);

  const columns: GridColDef[] = [
    boldLinkColumn<User, string>(
      {
        field: 'name',
        headerName: 'Name',
        valueGetter: (params) =>
          `${params.row.firstName || '--'} ${params.row.lastName || ''}`,
        sortable: false,
      },
      (user, _rowValue) => `/admin/users/${user?.id}`
    ),
    textColumn({
      field: 'externalId',
      headerName: 'User Id',
      sortable: false,
    }),
    textColumn({
      field: 'email',
      headerName: 'Email',
      sortable: false,
    }),
    {
      field: 'roleName',
      headerName: 'User Role',
      flex: 0.5,
      minWidth: 200,
      sortable: false,
    },
    dateColumn({
      field: 'createdAt',
      headerName: 'Created',
      sortable: false,
      valueGetter: (params) => new Date(params.row.createdAt),
    }),
    activeColumn({
      field: 'isActive',
      headerName: 'Active',
      valueGetter: (params) => !params.row.deactivatedAt,
      sortable: false,
    }),
  ];

  const tableArgs: TableProps<User> = {
    tableState: {
      columnModel: tableState['users'].columnModel,
      pinnedColumns: tableState['users'].pinnedColumns,
    },
    handleShowColumns: (newModel) => {
      if (!newModel) return;
      setTableState({
        ...tableState,
        users: { ...tableState['users'], columnModel: newModel },
      });
    },
    handlePinnedColumns: (newPinned) => {
      setTableState({
        ...tableState,
        users: { ...tableState['users'], pinnedColumns: newPinned },
      });
    },
    rows: data,
    columns,
    getRowId: (row) => (row.email ? row.email : uuidv4()),
    status,
    emptyEntityName: 'User',
    serverPaging: {
      ...usersData,
      onPageSizeChange: (newPageSize) => {
        onPageSizeChange(newPageSize);
      },
    },
  };

  return (
    <PageComponent
      title="Users"
      onSearch={onSearch}
      searchPlaceholder="Name"
      actions={
        permissions &&
        permissions?.can?.('put', 'users') && (
          <CreateNewEntityButton
            onClick={() => navigate('/admin/users/add')}
            entityName="User"
          />
        )
      }
    >
      <Box width="100%">
        <TableComponent {...tableArgs} />
      </Box>
    </PageComponent>
  );
}

export default Users;
